import { computed, effect, inject, Injectable, Signal, signal, untracked } from "@angular/core";
import { AppInsightsService } from "@api/services/app-insights/app-insights.service";
import { ICenter } from "@shared/models/center";
import { CentersDataService } from "@shared/services/api/centers-data/centers-data.service";
import { SessionStorageService } from "@shared/services/session-storage.service";
import { SessionStorageKeys } from "@shared/services/static/session-storage-keys";
import { Observable, shareReplay } from "rxjs";

@Injectable({
    providedIn: "root",
})
export class CentersService {
    private readonly dataService = inject(CentersDataService);
    private readonly sessionStorageService = inject(SessionStorageService);
    private readonly appInsightsService = inject(AppInsightsService);

    public centers$: Observable<ICenter[]> = this.dataService.getHealthCenters().pipe(shareReplay(1));

    private readonly _scopedCenter = signal<ICenter | null>(null);
    public scopedCenter: Signal<ICenter | null> = computed(() => {
        const scopedCenter = this._scopedCenter();

        if (scopedCenter) return scopedCenter;

        const storedScopedCenterStringified = this.sessionStorageService.getItem(SessionStorageKeys.ScopedCenter);

        if (!storedScopedCenterStringified) return null;

        try {
            return JSON.parse(storedScopedCenterStringified);
        } catch {
            return null;
        }
    });

    constructor() {
        effect(() => {
            const scopedCenter = this.scopedCenter();

            untracked(() => {
                if (scopedCenter) {
                    this.appInsightsService.setCenterContext(scopedCenter?.id);
                    this.sessionStorageService.setItem(
                        SessionStorageKeys.ScopedCenter,
                        JSON.stringify({ id: scopedCenter.id, name: scopedCenter.name }),
                    );
                }
            });
        });
    }

    public setScopedCenter(center: ICenter): void {
        const currentScopedCenter = this._scopedCenter();

        if (currentScopedCenter?.id === center.id) return;

        this._scopedCenter.set(center);
    }
}
